import { Button, Card, Flex, Input, InputNumber, Select, Space, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { OcrPhotoPicker } from '@/components/OcrPhotoPicker';
import { ValueScanner } from '@/components/ValueScanner';
import type { InventorizeReviewField } from '@/features/inventorization/domain/review/inventorize-review-fields';
import { messages } from '@/features/inventorization/messages';
import { ReviewStatusTag } from '@/routers/inventorization/components/ReviewStatusTag';
import type { ValueScanPurpose } from '@/shared/camera/value-scanner';
import type { OcrCandidateField } from '@/shared/ocr/ocr-candidates';

type MaybePromise<T> = T | Promise<T>;

export interface FieldWizardDefinition {
  currentValue: string;
  description: string;
  inputMode?: 'decimal' | 'text';
  ocrField?: OcrCandidateField;
  options?: Array<{ label: string; value: string }>;
  photoOcrPurpose?: ValueScanPurpose;
  placeholder: string;
  save: (value: string) => MaybePromise<unknown>;
  scanPurpose?: ValueScanPurpose;
  type: 'button-grid' | 'catalog' | 'native-select' | 'number' | 'options' | 'readonly' | 'text' | 'textarea' | 'width';
  widthUnit?: 'mm' | 'DIN';
}

const SELECT_WIZARD_MAX_HEIGHT = 320;
const BUTTON_GRID_OPTION_LIMIT = 12;

export function GenericFieldEditor({
  definition,
  field,
  onBack,
  setValue,
  sourcePhoto,
  value,
}: {
  definition: FieldWizardDefinition;
  field: InventorizeReviewField;
  onBack: () => void;
  setValue: (value: string) => void;
  sourcePhoto?: Blob;
  value: string;
}) {
  const intl = useIntl();
  const [isSaving, setIsSaving] = useState(false);

  async function saveAndReturn() {
    setIsSaving(true);

    try {
      await definition.save(value);
      onBack();
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Flex align="flex-start" gap="middle" justify="space-between" wrap>
          <div>
            <Typography.Text type="secondary">{field.section}</Typography.Text>
            <Typography.Title level={3} style={{ marginTop: 0 }}>
              {field.label}
            </Typography.Title>
            <Typography.Paragraph type="secondary">{definition.description}</Typography.Paragraph>
          </div>
          <ReviewStatusTag status={field.status} />
        </Flex>

        {definition.scanPurpose ? (
          <ValueScanner
            compact
            onAccept={candidate => setValue(candidate.value)}
            purpose={definition.scanPurpose}
            title={intl.formatMessage(messages.scannerValueCodeTitle)}
          />
        ) : null}

        <GenericFieldInput
          definition={definition}
          field={field}
          onSave={() => void saveAndReturn()}
          setValue={setValue}
          value={value}
        />

        {definition.photoOcrPurpose ? (
          <OcrPhotoPicker
            initialPhoto={sourcePhoto}
            onAccept={candidate => setValue(candidate.value)}
            purpose={definition.photoOcrPurpose}
          />
        ) : null}

        <Flex gap="small" justify="space-between" wrap>
          <Button onClick={onBack}>{intl.formatMessage(messages.reviewActionsBackToReview)}</Button>
          <Button
            disabled={definition.type === 'readonly' || isSaving}
            loading={isSaving}
            onClick={() => void saveAndReturn()}
            type="primary"
          >
            {intl.formatMessage(messages.reviewActionsSaveField)}
          </Button>
        </Flex>
      </Space>
    </Card>
  );
}

function GenericFieldInput({
  definition,
  field,
  onSave,
  setValue,
  value,
}: {
  definition: FieldWizardDefinition;
  field: InventorizeReviewField;
  onSave: () => void;
  setValue: (value: string) => void;
  value: string;
}) {
  const shouldUseButtonGrid =
    definition.type === 'button-grid' ||
    (definition.type === 'options' && (definition.options?.length ?? 0) < BUTTON_GRID_OPTION_LIMIT);

  if (definition.type === 'readonly') {
    return <Typography.Text type="secondary">{definition.placeholder}</Typography.Text>;
  }

  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      <Typography.Text>{field.label}</Typography.Text>
      {definition.type === 'native-select' ? (
        <SelectWizardInput definition={definition} setValue={setValue} value={value} variant="select-only" />
      ) : shouldUseButtonGrid ? (
        <ButtonGridInput definition={definition} setValue={setValue} value={value} />
      ) : definition.type === 'catalog' ? (
        <SelectWizardInput definition={definition} setValue={setValue} value={value} variant="custom-text" />
      ) : definition.type === 'number' ? (
        <NumberInput definition={definition} setValue={setValue} value={value} />
      ) : definition.type === 'width' ? (
        <WidthInput definition={definition} setValue={setValue} value={value} />
      ) : definition.options ? (
        <SelectWizardInput definition={definition} setValue={setValue} value={value} variant="select" />
      ) : definition.type === 'textarea' ? (
        <TextAreaInput definition={definition} setValue={setValue} value={value} />
      ) : (
        <TextInput definition={definition} onSave={onSave} setValue={setValue} value={value} />
      )}
    </Space>
  );
}

interface FieldInputControlProps {
  definition: FieldWizardDefinition;
  onSave: () => void;
  setValue: (value: string) => void;
  value: string;
}

function ButtonGridInput({ definition, setValue, value }: Omit<FieldInputControlProps, 'onSave'>) {
  return (
    <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
      {(definition.options ?? []).map(option => (
        <Button
          aria-pressed={value === option.value}
          key={option.value}
          onClick={() => setValue(option.value)}
          size="large"
          style={{
            height: 'auto',
            minHeight: 72,
            paddingBlock: 12,
            whiteSpace: 'normal',
          }}
          type={value === option.value ? 'primary' : 'default'}
        >
          <span style={{ overflowWrap: 'anywhere' }}>{option.label}</span>
        </Button>
      ))}
    </div>
  );
}

function NumberInput({ definition, setValue, value }: Omit<FieldInputControlProps, 'onSave'>) {
  return (
    <InputNumber
      inputMode={definition.inputMode === 'decimal' ? 'decimal' : 'numeric'}
      onChange={nextValue => setValue(nextValue?.toString() ?? '')}
      placeholder={definition.placeholder}
      style={{ width: '100%' }}
      value={value ? Number(value) : null}
    />
  );
}

function WidthInput({ definition, setValue, value }: Omit<FieldInputControlProps, 'onSave'>) {
  const width = readWidthInputValue(value, definition.widthUnit);

  return (
    <InputNumber
      addonAfter={
        <Select
          options={[
            { label: 'mm', value: 'mm' },
            { label: 'DIN', value: 'DIN' },
          ]}
          onChange={unit => setValue(formatWidthInputValue(width.value, unit))}
          style={{ width: 80 }}
          value={width.unit}
        />
      }
      inputMode="decimal"
      onChange={nextValue => setValue(formatWidthInputValue(nextValue?.toString() ?? '', width.unit))}
      placeholder={definition.placeholder}
      style={{ width: '100%' }}
      value={width.value ? Number(width.value.replace(',', '.')) : null}
    />
  );
}

function TextAreaInput({ definition, setValue, value }: Omit<FieldInputControlProps, 'onSave'>) {
  return (
    <Input.TextArea
      autoSize={{ minRows: 4, maxRows: 8 }}
      onChange={event => setValue(event.target.value)}
      placeholder={definition.placeholder}
      value={value}
    />
  );
}

function TextInput({ definition, onSave, setValue, value }: FieldInputControlProps) {
  return (
    <Input
      inputMode={definition.inputMode ?? 'text'}
      onChange={event => setValue(event.target.value)}
      onPressEnter={() => onSave()}
      placeholder={definition.placeholder}
      value={value}
    />
  );
}

function SelectWizardInput({
  definition,
  setValue,
  value,
  variant,
}: {
  definition: FieldWizardDefinition;
  setValue: (value: string) => void;
  value: string;
  variant: 'custom-text' | 'select' | 'select-only';
}) {
  const intl = useIntl();
  const options = definition.options ?? [];
  const selectedOption = useMemo(
    () => (variant === 'custom-text' ? options.find(option => option.value === value) : undefined),
    [options, value, variant],
  );
  const [query, setQuery] = useState(selectedOption?.label ?? value);
  const visibleOptions = useMemo(() => {
    if (variant !== 'custom-text') {
      return options;
    }

    const normalizedQuery = query.trim().toLowerCase();

    return options.filter(option => !normalizedQuery || option.label.toLowerCase().includes(normalizedQuery));
  }, [options, query, variant]);
  const selectValue = value ? value : undefined;

  useEffect(() => {
    if (variant === 'custom-text') {
      setQuery(selectedOption?.label ?? value);
    }
  }, [selectedOption?.label, value, variant]);

  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      {variant === 'custom-text' ? (
        <Input
          onChange={event => {
            setQuery(event.target.value);
            setValue(event.target.value);
          }}
          placeholder={definition.placeholder}
          value={query}
        />
      ) : (
        <Select
          allowClear
          options={options}
          onChange={nextValue => setValue(nextValue ?? '')}
          optionFilterProp="label"
          placeholder={definition.placeholder}
          showSearch
          style={{ width: '100%' }}
          value={selectValue}
        />
      )}
      {variant === 'select-only' ? null : (
        <Space
          direction="vertical"
          size="small"
          style={{ maxHeight: SELECT_WIZARD_MAX_HEIGHT, overflowY: 'auto', width: '100%' }}
        >
          {visibleOptions.map(option => (
            <Button
              block={variant === 'custom-text'}
              key={option.value}
              onClick={() => {
                setValue(option.value);

                if (variant === 'custom-text') {
                  setQuery(option.label);
                }
              }}
              style={{ textAlign: variant === 'custom-text' ? 'left' : undefined }}
              type={value === option.value ? 'primary' : 'default'}
            >
              {option.label}
            </Button>
          ))}
          {visibleOptions.length === 0 ? (
            <Typography.Text type="secondary">
              {intl.formatMessage(messages.fieldWizardCatalogNoOptionsMatch)}
            </Typography.Text>
          ) : null}
        </Space>
      )}
    </Space>
  );
}

function readWidthInputValue(value: string, fallbackUnit: FieldWizardDefinition['widthUnit']) {
  const trimmedValue = value.trim();
  const unitMatch = trimmedValue.match(/\s(mm|DIN)$/i);
  const unit = unitMatch?.[1]?.toUpperCase() === 'DIN' ? 'DIN' : (fallbackUnit ?? 'mm');
  const rawValue = unitMatch ? trimmedValue.slice(0, unitMatch.index).trim() : trimmedValue;

  return {
    unit,
    value: rawValue,
  };
}

function formatWidthInputValue(value: string, unit: 'mm' | 'DIN') {
  const trimmedValue = value.trim();

  return trimmedValue ? `${trimmedValue} ${unit}` : '';
}
